import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Building2, Users, Camera } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import { fetchChats, type ChatSummary } from '@/lib/queries';
import { cn } from '@/lib/cn';

/** Three layers: the company room, one room per clinic, and every creative
 *  with a chat. Newest message first inside each layer, with unread counts. */
export default function ChatsPage() {
  const q = useQuery({ queryKey: ['chats'], queryFn: fetchChats, refetchInterval: 15000 });
  const all = q.data ?? [];
  const unread = all.filter((c) => c.kind !== 'clinic').reduce((n, c) => n + c.unread, 0);
  const company = all.filter((c) => c.kind === 'company');
  const clinics = all.filter((c) => c.kind === 'client');   // the team room; client chats have their own page
  const creatives = all.filter((c) => c.kind === 'creative');
  const shoots = all.filter((c) => c.kind === 'shoot');
  return (
    <div className="max-w-4xl">
      <PageHeader title="Chats" subtitle={unread ? `${unread} unread message${unread === 1 ? '' : 's'}` : 'The company room, each clinic room, and every creative.'} />
      {q.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {company.length > 0 && <Section title="Company" hint="Everyone at KAF Warehouse" rows={company} />}
      {clinics.length > 0 && <Section title="Clinics" hint="The team on each clinic, internal only. Talking with the clinic itself is on Client chats." rows={clinics} />}
      {shoots.length > 0 && <Section title="On set" hint="The feed of each shoot: photos and clips from the phone, answered by the art director." rows={shoots} />}
      <Section title="Creatives" hint={creatives.length ? 'Newest message first' : 'No creative chats yet. Every creative has one at the bottom of its page.'} rows={creatives} />
    </div>
  );
}

function linkFor(c: ChatSummary): string {
  if (c.kind === 'company') return '/chats/company';
  if (c.kind === 'client') return `/chats/client/${c.client_id}`;
  if (c.kind === 'shoot') return `/shoots/${c.shoot_id}#feed`;
  return `/creatives/${c.creative_id}#chat`;
}

function Section({ title, hint, rows }: { title: string; hint: string; rows: ChatSummary[] }) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline gap-2 mb-2">
        <h2 className="text-xs uppercase tracking-wide text-muted">{title}</h2>
        <span className="text-[11px] text-muted/70">{hint}</span>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <Link key={c.thread} to={linkFor(c)} className="block" data-testid="chat-row">
            <Card className={cn('hover:border-accent-400/40 transition', c.unread > 0 && 'border-accent-500/40')}>
              <div className="flex items-start gap-3">
                <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0', c.unread ? 'bg-accent-600/20 text-accent-400' : 'bg-slate-800 text-muted')}>
                  {c.kind === 'company' ? <Users size={14} /> : c.kind === 'client' ? <Building2 size={14} /> : c.kind === 'shoot' ? <Camera size={14} /> : <MessageSquare size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.code && <span className="font-mono text-[10px] text-muted">{c.code}</span>}
                    <span className="text-sm font-medium text-muted-strong truncate">{c.title}</span>
                    {(c.kind === 'creative' || c.kind === 'shoot') && <span className="text-[11px] text-muted">· {c.client_name}</span>}
                    {c.unread > 0 && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-accent-500 text-white">{c.unread} new</span>}
                  </div>
                  <div className="text-xs text-muted truncate mt-0.5">
                    {c.last_author ? (<><span className="text-muted-strong">{c.last_author}</span>: {c.last_body}</>) : <span className="italic">No messages yet</span>}
                    {c.last_at && <span className="ml-2 text-[10px]">{new Date(c.last_at).toLocaleString('en-AE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Dubai' })}</span>}
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
